export type Condition<T> = {
    not?: boolean;
    operator: ConditionOperator;
    elements?: Array<T>;
    children?: Array<Condition<T>>;
};

export enum ConditionOperator {
    AND = "AND",
    OR = "OR",
}

function inArrayChecker<T>(available: Array<T>) {
    return (elementToCheck: T) => {
        return available.indexOf(elementToCheck) !== -1;
    };
}

export function hasOneOf<T>(available: Array<T>, needed: Array<T>): boolean {
    return hasOneOfAdvanced(needed, inArrayChecker(available));
}

export function hasAll<T>(available: Array<T>, needed: Array<T>): boolean {
    return hasAllAdvanced(needed, inArrayChecker(available));
}

export function hasOneOfAdvanced<T>(
    elementsToCheck: Array<T>,
    checker: (elementToCheck: T) => boolean
) {
    if (elementsToCheck.length === 0) {
        return true;
    }

    for (const elementToCheck of elementsToCheck) {
        if (checker(elementToCheck)) {
            return true;
        }
    }

    return false;
}

export function hasAllAdvanced<T>(
    elementsToCheck: Array<T>,
    checker: (elementToCheck: T) => boolean
): boolean {
    if (elementsToCheck.length === 0) {
        return true;
    }

    for (const elementToCheck of elementsToCheck) {
        if (!checker(elementToCheck)) {
            return false;
        }
    }

    return true;
}

function conditionChecker<T>(available: Array<T>, condition: Condition<T>): boolean {
    let functionToCheckConditions = hasOneOfAdvanced;

    if (condition.operator === ConditionOperator.AND) {
        functionToCheckConditions = hasAllAdvanced;
    }

    const conditions: Array<boolean> = [];

    if (condition.elements !== undefined && condition.elements.length > 0) {
        conditions.push(functionToCheckConditions(condition.elements, inArrayChecker(available)));
    }

    if (condition.children !== undefined && condition.children.length > 0) {
        conditions.push(
            functionToCheckConditions(condition.children, (elementToCheck: Condition<T>) =>
                conditionChecker(available, elementToCheck)
            )
        );
    }

    const check = functionToCheckConditions(conditions, (elementsToCheck) => elementsToCheck);

    if (condition.not) {
        return !check;
    }

    return check;
}

export default conditionChecker;
